import React, { useCallback } from 'react';
import {
  BillingDisputesTab,
  BillingHeader,
  BillingHistoryTab,
  BillingHoursTab,
  BillingSummaryTab,
} from '@pay-bill/components';
import {
  AppContent,
  ErrorHandler,
  LoadingState,
  Tabs,
} from '@common/components';
import { useRevInvBreadcrumbsEffect } from '@pay-bill/hooks';
import { useQuery, useNavigate, useParams } from '@common/hooks';
import {
  RevInvoiceReviewQuery,
  RevInvoiceReviewQueryArgs,
  RevInvoiceReviewQueryResponse,
} from '@pay-bill/graphql';

export const ReverseInvoicePage = () => {
  const navigate = useNavigate();
  const { id, tabKey = 'summary' } = useParams<{
    id: string;
    tabKey: 'summary' | 'hours' | 'disputes' | 'history';
  }>();

  const { data, loading, error } = useQuery<
    RevInvoiceReviewQueryResponse,
    RevInvoiceReviewQueryArgs
  >(RevInvoiceReviewQuery, {
    variables: { id },
  });

  const handleTabChange = useCallback(
    (tabKey: string) => {
      navigate(`/works/supplier/billing/reverse-invoices/${id}/${tabKey}`);
    },
    [navigate, id]
  );

  const reverseInvoice = data?.worksReverseInvoice;

  useRevInvBreadcrumbsEffect(reverseInvoice);

  if (error) {
    return <ErrorHandler error={error} log="InvoicePage" />;
  }

  if (loading || !reverseInvoice) {
    return <LoadingState />;
  }

  return (
    <AppContent>
      <BillingHeader reverseInvoiceData={reverseInvoice} isSupplier />
      <Tabs
        activeKey={tabKey}
        defaultActiveKey={tabKey}
        onChange={handleTabChange}
      >
        <Tabs.Pane tab="Summary" key="summary">
          <BillingSummaryTab reverseInvoiceData={reverseInvoice} isSupplier />
        </Tabs.Pane>
        <Tabs.Pane tab="Hours" key="hours">
          <BillingHoursTab id={id} isSupplier />
        </Tabs.Pane>
        <Tabs.Pane tab="Disputes" key="disputes">
          <BillingDisputesTab isSupplier revInvoiceCode={reverseInvoice.code} />
        </Tabs.Pane>
        <Tabs.Pane tab="History" key="history">
          <BillingHistoryTab id={id} isSupplier />
        </Tabs.Pane>
      </Tabs>
    </AppContent>
  );
};
